import { attr, css, FASTElement, html, Observable } from '@microsoft/fast-element';
import { brand, designTokens } from '../../design-tokens';
import { display } from '../../foundation/display';
import { NatGenDesignSystem } from '../../natgen-design-system';
import { DesignToken, DesignTokenChangeRecord, DesignTokenSubscriber } from '../../foundation/design-token';
import { backgroundColorContext } from '../../context/background-context';

export class NatGenLogo extends FASTElement implements DesignTokenSubscriber<DesignToken<any>> {
    @attr
    brand: string;

    @attr
    src: string;

    @attr
    size: number;

    @attr
    padding: number;

    @backgroundColorContext
    isDarkMode: boolean;

    connectedCallback(): void {
        super.connectedCallback();
        brand.subscribe(this);
    }

    handleChange(token: DesignToken<any>, record: DesignTokenChangeRecord<DesignToken<any>>): void {
        Observable.notify(this, 'logo');
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        brand.unsubscribe(this);
    }

    getTokenName() : string {
        const currentBrand = this.brand || brand.getValueFor(this);
        let token = `branding-logos-${currentBrand}`

        if (this.isDarkMode && designTokens[`${token}-dark-image`])
            token += '-dark';

        return token;
    }

    getLogoPath() : string {
        if (this.src)
            return this.src;

        Observable.track(this, 'logo');

        return `${process.env.ASSET_PATH ? process.env.ASSET_PATH : ''}branding/${designTokens[`${this.getTokenName()}-image`].getValueFor(this)}`;
    }

    hasDefinedHeight() : boolean {
        if (this.size)
            return true;

        Observable.track(this, 'logo');

        return `${this.getTokenName()}-size` in designTokens;
    }

    getLogoHeightStyle() : string {
        if (this.size)
            return `height: ${this.size * designTokens['space-design-unit'].getValueFor(this)}px;`

        Observable.track(this, 'logo');

        return this.hasDefinedHeight() ?
            `height: ${(designTokens[`${this.getTokenName()}-size`]?.getValueFor(this) || 10) * designTokens['space-design-unit'].getValueFor(this)}px;` : '';
    }

    hasDefinedPadding() : boolean {
        if (this.padding)
            return true;

        Observable.track(this, 'logo');

        return `${this.getTokenName()}-padding` in designTokens;
    }

    getLogoPaddingStyle() : string {
        if (this.padding)
            return `margin: ${this.padding * designTokens['space-design-unit'].getValueFor(this)}px;`

        Observable.track(this, 'logo');

        return this.hasDefinedPadding() ?
            `margin: ${(designTokens[`${this.getTokenName()}-padding`]?.getValueFor(this) || 0) * designTokens['space-design-unit'].getValueFor(this)}px;` : '';
    }
}

export const natGenLogoDefinition = NatGenLogo.compose({
    name: `${NatGenDesignSystem.prefix}-logo`,
    template: html<NatGenLogo>`
        <img style="${x => x.getLogoHeightStyle()} ${x => x.getLogoPaddingStyle()}" src="${x => x.getLogoPath()}" />
    `,
    styles: css`
        ${display('block')}
    `
});
